
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineBookOpen,
  HiOutlineArrowUpRight,
} from "react-icons/hi2";

import { getAllBlogs } from "../redux/actions/blogs";
import { Loader } from "./utils";
import BlogsCard from "./utils/BlogsCard";

/* ============================================
   RELATED BLOG ARTICLES
============================================ */

function RelatedBigBlogs({ currentBlogId }) {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const { isLoading, AllBlogs } = useSelector(
    (state) => state.Blogs || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  /* ======================================
     FETCH RELATED ARTICLES
  ====================================== */

  useEffect(() => {
    // Load extra articles to allow excluding
    // the article currently being viewed.
    dispatch(getAllBlogs(0, 6));
  }, [dispatch]);

  const articles = Array.isArray(AllBlogs)
    ? AllBlogs
        .filter(
          (blog) =>
            String(blog._id) !== String(currentBlogId)
        )
        .slice(0, 3)
    : [];

  const content = {
    eyebrow: isEnglish
      ? "CONTINUE EXPLORING"
      : "واصل القراءة",

    title: t("Related Blogs"),

    description: isEnglish
      ? "Explore more legal articles and insights from our knowledge library."
      : "اكتشف المزيد من المقالات والرؤى القانونية التي تتناول موضوعات متنوعة.",

    all: isEnglish
      ? "Browse All Articles"
      : "تصفح جميع المقالات",
  };

  if (!isLoading && articles.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="related-articles-heading"
      className="
        relative
        overflow-hidden

        border-t border-[#D9E5EC]
        bg-[#EAF0F5]

        py-16
        md:py-20
        lg:py-24

        dark:border-white/10
        dark:bg-[#13293B]

        transition-colors duration-300
      "
    >
      <div className="container">

        {/* ======================================
            HEADER
        ====================================== */}

        <div
          className="
            mb-10 flex flex-col
            justify-between gap-6

            md:flex-row
            md:items-end
          "
        >
          <div className="max-w-[700px]">

            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-9 bg-skin-accent" />

              <span className="eyebrow text-skin-accent">
                {content.eyebrow}
              </span>
            </div>

            <h2
              id="related-articles-heading"
              className="section-title text-skin-heading"
            >
              {content.title}
            </h2>

            <p className="body-2 mt-4 text-skin-muted">
              {content.description}
            </p>

          </div>

          {/* View all */}
          <Link
            to="/BlogsCategory"
            onClick={() => window.scrollTo(0, 0)}
            className="
              group inline-flex
              items-center gap-3
              self-start

              rounded-xl
              border border-skin-border
              bg-skin-surface
              px-5 py-3

              text-skin-heading

              transition-all duration-300

              hover:border-skin-accent/40
              hover:bg-skin-soft

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            <span className="button-sm">
              {content.all}
            </span>

            <HiOutlineArrowUpRight
              size={19}
              aria-hidden="true"
              className="directional-arrow text-skin-accent"
            />
          </Link>

        </div>

        {/* ======================================
            RELATED ARTICLES GRID
        ====================================== */}

        {isLoading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div
            className="
              grid grid-cols-1
              items-stretch gap-6

              md:grid-cols-2
              lg:grid-cols-3

              lg:gap-7
            "
          >
            {articles.map((blog, index) => (
              <BlogsCard
                key={blog._id || blog.id || index}
                blogs={blog}
                variant="default"
                index={index}
              />
            ))}
          </div>
        )}

      </div>
    </section>
  );
}

export default RelatedBigBlogs;
